import React from 'react';
import { Zap, Cpu, HeartPulse, Clock, MapPin, ArrowUpRight, Check } from 'lucide-react';
import { TRAINING_TRACKS } from '../mock';

const iconMap = { Zap, Cpu, HeartPulse };

const TrackCard = ({ track }) => {
  const Icon = iconMap[track.icon] || Zap;
  return (
    <div className="group relative rounded-3xl border border-slate-200 bg-white p-6 lg:p-8 hover:border-slate-300 hover:shadow-xl hover:shadow-slate-900/5 transition-all duration-300 flex flex-col">
      <div className="flex items-start justify-between">
        <div className="w-12 h-12 rounded-2xl bg-slate-900 flex items-center justify-center">
          <Icon size={22} className="text-white" strokeWidth={2} />
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 text-[11.5px] font-medium px-2.5 py-1 border border-amber-100">
          <Clock size={12} /> {track.duration}
        </span>
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-[12px] text-slate-500">
        <MapPin size={11} /> {track.format}
      </div>

      <h3 className="mt-4 font-display text-[22px] font-semibold text-slate-900 tracking-tight">
        {track.title}
      </h3>
      <p className="mt-2 text-[14.5px] leading-relaxed text-slate-600">
        {track.description}
      </p>

      <div className="mt-5 pt-5 border-t border-slate-100">
        <div className="text-[11.5px] font-medium tracking-[0.14em] text-slate-400 uppercase">
          Outcomes
        </div>
        <ul className="mt-3 space-y-2">
          {track.outcomes.map((o) => (
            <li key={o} className="flex items-start gap-2 text-[13.5px] text-slate-700">
              <Check size={15} className="mt-0.5 text-emerald-500 flex-none" strokeWidth={2.5} />
              <span>{o}</span>
            </li>
          ))}
        </ul>
      </div>

      <a
        href="#contact"
        className="mt-6 inline-flex items-center gap-1 text-[13.5px] font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
      >
        Enquire about this track
        <ArrowUpRight
          size={15}
          className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        />
      </a>
    </div>
  );
};

const Training = () => {
  return (
    <section id="training" className="relative py-24 lg:py-32 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[12px] font-medium text-amber-800 uppercase tracking-[0.18em]">
              Training & Hackathons
            </div>
            <h2 className="mt-4 font-display text-[36px] md:text-[52px] leading-[1.05] font-semibold tracking-tight text-slate-900">
              Upskill teams that{' '}
              <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">
                actually ship.
              </span>
            </h2>
            <p className="mt-5 text-[16.5px] leading-relaxed text-slate-600">
              Hands-on workshops, cross-functional hackathons and embedded coaching - built around real problems in your environment, not toy examples.
            </p>
          </div>
          <a
            href="#contact"
            className="inline-flex items-center gap-1.5 self-start lg:self-end px-5 py-3 rounded-xl border border-slate-200 bg-white text-slate-800 font-medium text-[14px] hover:border-slate-300 hover:bg-slate-50 transition-colors"
          >
            Design a custom cohort
            <ArrowUpRight size={15} />
          </a>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TRAINING_TRACKS.map((t) => (
            <TrackCard key={t.id} track={t} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Training;
